import {
  CeilingTray,
  FeaturePatternTrussContainer,
  LayoutFeatureArea,
  Segment2D as LineSegment2DInterface,
} from '@paragon/contract-library';
import { LineSegment2D } from '@paragon/geometry-library';
import { DisplayablePlaneWithCalculatedGeometry, isRoofPlane } from '@paragon/layout';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { useAppSelector } from '../../../../components/hooks';
import { getSelectedItemIdentifiers } from '../../../../library/selection/selectors';
import { getInsertionCeilingTray } from '../../../../store/selectors/ceilingTraySelectors';
import { getCurrentlySelectableItemTypes } from '../../../../store/selectors/getCurrentlyClickableItemTypes';
import { getPickingObjectInfo } from '../../../../store/selectors/pickingSelectors';
import {
  getCurrentlyPickablePlaneGuids,
  getInsertionCeilingPlane,
  getInsertionRoofPlane,
} from '../../../../store/selectors/roofAndCeilingPlaneSelectors';
import { layoutColors } from '../../layoutColors';

type StyledFaceType = keyof (typeof layoutColors)['roofFaces']['hues'];
type StyledFaceTint = keyof (typeof layoutColors)['roofFaces']['tints'];

export type LayoutPolygonStyle = `${StyledFaceType}.${StyledFaceTint}`;

export function getRoofFaceColor(style: LayoutPolygonStyle): string {
  const [type, tint] = style.split('.') as [StyledFaceType, StyledFaceTint];
  const tintKey = layoutColors.roofFaces.tints[tint];

  return tint === 'highlighted'
    ? layoutColors.roofFaces.highlightHue[tintKey]
    : layoutColors.roofFaces.hues[type][tintKey];
}

export const usePlaneStyleCalculator = () => {
  const highlightedPlane = useAppSelector((state) => state.layout.highlightedPlane);
  const hoveredRoofFace = useAppSelector((state) => state.layout.hoveredRoofFace);

  const selectedItems = useAppSelector(getSelectedItemIdentifiers);
  const selectedPlanes = selectedItems.filter((item) => item.type === 'roofPlane' || item.type === 'ceilingPlane');
  const selectedCeilingTrays = selectedItems.filter((item) => item.type === 'ceilingTray');

  const insertionRoofPlane = useAppSelector(getInsertionRoofPlane);
  const insertionCeilingPlane = useAppSelector(getInsertionCeilingPlane);

  const pickingObjectInfo = useAppSelector(getPickingObjectInfo);

  const selectableItemTypes = useAppSelector(getCurrentlySelectableItemTypes);
  const pickablePlaneGuids = useAppSelector(getCurrentlyPickablePlaneGuids);

  return function getStyle(plane: DisplayablePlaneWithCalculatedGeometry): LayoutPolygonStyle {
    const type: StyledFaceType = isRoofPlane(plane) ? 'roof' : 'ceiling';

    const style = (tint: StyledFaceTint): LayoutPolygonStyle => `${type}.${tint}`;

    // An insertion plane is always emphasised.
    if ('guid' in plane) {
      const isInsertionRoofPlane = insertionRoofPlane && plane.guid === insertionRoofPlane.guid;
      const isInsertionCeilingPlane = insertionCeilingPlane && plane.guid === insertionCeilingPlane.guid;

      if (isInsertionRoofPlane || isInsertionCeilingPlane) {
        return style('emphasised');
      }
    }

    const selectedPlanesOfSameType = selectedPlanes.filter((item) => item.type === `${type}Plane`);

    const isSelected = selectedPlanesOfSameType.some((item) => 'guid' in plane && item.guid === plane.guid);
    const otherRoofFacesAreSelected =
      selectedPlanesOfSameType.some((item) => !('guid' in plane) || item.guid !== plane.guid) ||
      (type === 'ceiling' && selectedCeilingTrays.length > 0);

    // Hover based effects -- there are three modes that we can be in, all mutually exclusive, so check them in order of
    //   priority.

    // 1. Highlighting to indicate a hovered plane is pickable when in picking mode
    if (
      'guid' in plane &&
      pickablePlaneGuids.has(plane.guid) &&
      pickingObjectInfo?.type === 'pickingPlane' &&
      pickingObjectInfo.activeMode === 'real'
    ) {
      if (
        pickingObjectInfo.hoveredPlane?.type !== 'absoluteVertical' &&
        pickingObjectInfo.hoveredPlane?.guid === plane.guid
      ) {
        return style('highlighted');
      }
    }

    // 2. Highlighting a plane of interest (at the request of various systems) to make it more easily identifiable.
    else if (highlightedPlane) {
      switch (highlightedPlane.type) {
        case 'absoluteVertical': {
          if (
            plane.geometry.type === 'Absolute' &&
            lineSegmentsEqual(plane.geometry.segment, highlightedPlane.segment)
          ) {
            return style('highlighted');
          }
          break;
        }

        case 'ceilingPlane': {
          if ('guid' in plane && !isRoofPlane(plane) && plane.guid === highlightedPlane.guid) {
            return style('highlighted');
          }
          break;
        }

        case 'roofPlane': {
          if ('guid' in plane && isRoofPlane(plane) && plane.guid === highlightedPlane.guid) {
            return style('highlighted');
          }
          break;
        }

        default:
          assertExhaustiveSwitch(highlightedPlane);
      }
    }

    // 3. Indicating that a plane is selectable when it is hovered (and none of the above states are active).
    else if (hoveredRoofFace) {
      if (
        'guid' in plane &&
        hoveredRoofFace.type === 'roofOrCeilingPlane' &&
        hoveredRoofFace.guid === plane.guid &&
        !isSelected &&
        (isRoofPlane(plane) ? selectableItemTypes.has('roofPlane') : selectableItemTypes.has('ceilingPlane'))
      ) {
        return style(otherRoofFacesAreSelected ? 'fadedButBarelyEmphasised' : 'barelyEmphasised');
      }
    }

    // styles based on selection status
    if (isSelected) return style('emphasised');
    if (otherRoofFacesAreSelected || insertionRoofPlane) return style('faded');

    return style('normal');
  };
};

export const useTrayStyleCalculator = () => {
  const hoveredRoofFace = useAppSelector((state) => state.layout.hoveredRoofFace);

  const selectableItemTypes = useAppSelector(getCurrentlySelectableItemTypes);

  const selectedItems = useAppSelector(getSelectedItemIdentifiers);
  const selectedTrays = selectedItems.filter((item) => item.type === 'ceilingTray');
  const selectedPlanes = selectedItems.filter((item) => item.type === 'ceilingPlane' || item.type === 'roofPlane');

  const insertionCeilingTray = useAppSelector(getInsertionCeilingTray);

  return function getStyle(tray: CeilingTray | Omit<CeilingTray, 'guid'>): LayoutPolygonStyle {
    // An insertion tray is always emphasised.
    if (!('guid' in tray)) {
      return 'ceiling.emphasised';
    }

    const isSelected = selectedTrays.some((item) => item.guid === tray.guid);
    const otherRoofFacesAreSelected =
      selectedTrays.some((item) => item.guid !== tray.guid) || selectedPlanes.length > 0;

    if (
      !isSelected &&
      hoveredRoofFace &&
      selectableItemTypes.has('ceilingTray') &&
      hoveredRoofFace.type === 'ceilingTrayPolygon' &&
      hoveredRoofFace.guid === tray.guid
    ) {
      return otherRoofFacesAreSelected ? 'ceiling.fadedButBarelyEmphasised' : 'ceiling.barelyEmphasised';
    }

    if (isSelected) return 'ceiling.emphasised';
    if (otherRoofFacesAreSelected || insertionCeilingTray) return 'ceiling.faded';

    return 'ceiling.normal';
  };
};

export const useFeaturePatternContainerStyleCalculator = () => {
  const selectedItems = useAppSelector(getSelectedItemIdentifiers);

  const selectedContainers = selectedItems.filter((item) => item.type === 'featurePatternContainer');
  const selectedFeatureAreas = selectedItems.filter((item) => item.type === 'featureArea');

  const insertionContainer = useAppSelector((state) => {
    const insertionObjectInfo = state.application.insertingObjectInfo;
    return insertionObjectInfo?.type === 'featurePatternContainer' ? insertionObjectInfo.data : null;
  });

  return function getStyle(container: FeaturePatternTrussContainer): LayoutPolygonStyle {
    const isSelected = selectedContainers.some((item) => item.guid === container.guid);
    const otherContainersAreSelected = selectedContainers.some((item) => item.guid !== container.guid);

    if (isSelected) return 'featurePattern.emphasised';
    if (otherContainersAreSelected || insertionContainer || selectedFeatureAreas.length > 0)
      return 'featurePattern.faded';

    return 'featurePattern.normal';
  };
};

export const useFeatureAreaStyleCalculator = () => {
  const selectedAreas = useAppSelector(getSelectedItemIdentifiers).filter((item) => item.type === 'featureArea');

  const insertionArea = useAppSelector((state) => {
    const insertionObjectInfo = state.application.insertingObjectInfo;
    return insertionObjectInfo?.type === 'featureArea' ? insertionObjectInfo.data : null;
  });

  return function getStyle(area: LayoutFeatureArea): LayoutPolygonStyle {
    const isSelected = selectedAreas.some((item) => item.guid === area.guid);
    const otherAreasAreSelected = selectedAreas.some((item) => item.guid !== area.guid);

    if (isSelected) return 'featureArea.emphasised';
    if (otherAreasAreSelected || insertionArea) return 'featureArea.faded';

    return 'featureArea.normal';
  };
};

function lineSegmentsEqual(a: LineSegment2DInterface, b: LineSegment2DInterface) {
  return LineSegment2D.fromLineSegmentObject(a).equals(LineSegment2D.fromLineSegmentObject(b));
}
